export type MedicalNecessityIndicator = 'approved' | 'missing' | 'not-approved' | null;

/**
 * Green/yellow/red medical necessity indicator, derived from an order's lcd_coverage /
 * lcd_coverage_details:
 *   approved      → LCD coverage confirmed
 *   missing       → policy or insurance data missing (check couldn't run)
 *   not-approved  → check ran and coverage was not confirmed
 *
 * Single source of truth so the Action Items panel and the medical-necessity view modal
 * never drift apart on how this is classified.
 */
export function getMedicalNecessityIndicator(order: any): MedicalNecessityIndicator {
  if (!order || order.lcd_coverage_details == null) return null;
  if (order.lcd_coverage) return 'approved';
  const details = String(order.lcd_coverage_details).toLowerCase();
  // Every canned message medicalNecessityCheckForNewOrder writes when the check itself
  // couldn't complete (as opposed to actually running and determining no coverage) — keep
  // this in sync with app/helpers/aiHelper.js.
  const missingReasons = [
    'no insurance plan found',
    'no cpt codes are available',
    'lcd file not found',
    'lcd text not found',
    'ai call failed',
    'unexpected error during medical necessity check',
  ];
  if (missingReasons.some((reason) => details.includes(reason))) return 'missing';
  return 'not-approved';
}

export function getMedicalNecessityIndicatorLabel(indicator: MedicalNecessityIndicator): string {
  switch (indicator) {
    case 'approved':
      return 'Approved';
    case 'missing':
      return 'Missing Policy / Insurance';
    case 'not-approved':
      return 'Not Approved';
    default:
      return '';
  }
}

export interface CoverageBlock {
  type: 'paragraph' | 'numbered' | 'bullet';
  number?: string;
  label?: string;
  text: string;
}

export interface CoverageDetailRow {
  label: string;
  value: string;
  blocks: CoverageBlock[];
}

/**
 * The AI-generated coverage explanation is actually structured — numbered criteria
 * ("1. Indication Requirement (...)"), "- " sub-bullets with policy/chart citations, short
 * labeled sections ("Requirement:", "ORT_MASTERS facts:", "Summary:"), and a closing
 * "Therefore, ..." verdict — but that structure is conveyed only via runs of whitespace
 * (mostly double spaces, occasionally a single space) rather than real newlines. This
 * recovers the structure instead of flattening it into plain sentences.
 *
 * Single source of truth so every place that renders lcd_coverage_details (the document
 * viewer modal, the ORT Masters tab's inline Medical Necessity section, ...) reads it
 * identically.
 */
export function parseCoverageText(rawText: string): CoverageBlock[] {
  if (!rawText) return [];

  let text = rawText.replace(/\r\n/g, '\n').trim();
  text = text.replace(/[ \t]{2,}/g, '\n');
  // A couple of boundaries only carry a single space (e.g. "Relevant snippets: - Policy:
  // ..."), so also break immediately before a bullet/numbered marker wherever it appears.
  text = text.replace(/ (?=-\s+[A-Z])/g, '\n');
  text = text.replace(/ (?=\d{1,2}\.\s+[A-Z])/g, '\n');

  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

  return lines.map((line): CoverageBlock => {
    const numbered = line.match(/^(\d{1,2})\.\s+(.*)$/);
    if (numbered) return { type: 'numbered', number: numbered[1], text: numbered[2] };

    const bullet = line.match(/^[-•]\s+(.*)$/);
    if (bullet) return { type: 'bullet', text: bullet[1] };

    // A short "Label:" / "Two Word Label:" prefix starting its own line (e.g. "Summary:",
    // "ORT_MASTERS facts:") — up to 4 words before the colon keeps this from matching
    // ordinary sentences that merely contain a colon further in.
    const labeled = line.match(/^([A-Z][A-Za-z_]*(?:\s[A-Za-z_]+){0,3}:)\s*(.*)$/);
    if (labeled) return { type: 'paragraph', label: labeled[1], text: labeled[2] || '' };

    return { type: 'paragraph', text: line };
  });
}

/**
 * Parses an order's lcd_coverage_details into one-or-more labeled rows of structured
 * CoverageBlocks. Handles the plain-English case (a single "Coverage Notes" row) and the
 * rarer case where lcd_coverage_details is itself a JSON object with multiple keyed
 * explanations.
 */
export function buildCoverageDetails(rawDetails: any): CoverageDetailRow[] {
  let details = rawDetails;
  if (typeof details === 'string') {
    const trimmed = details.trim();
    const asPlainText = () =>
      trimmed ? [{ label: 'Coverage Notes', value: trimmed, blocks: parseCoverageText(trimmed) }] : [];
    // Only attempt JSON parsing if the text actually looks like a JSON object — otherwise
    // a plain-English explanation that happens to be JSON.parse-able on its own (e.g. a
    // bare number, or literally "null"/"true") would silently parse into a non-object and
    // fall through the `typeof details !== 'object'` check below, showing nothing at all.
    if (!trimmed.startsWith('{')) return asPlainText();
    try {
      details = JSON.parse(trimmed);
    } catch {
      return asPlainText();
    }
  }
  if (!details || typeof details !== 'object') return [];

  return Object.keys(details)
    .filter((key) => details[key] != null)
    .map((key) => {
      const value = typeof details[key] === 'object' ? JSON.stringify(details[key]) : String(details[key]);
      return {
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        value,
        blocks: parseCoverageText(value),
      };
    });
}
